<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {ReferenceWidgetProps} from "@/utils/presenters/interfaces.ts";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import {onBeforeMount, ref} from "vue";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;

const props = defineProps<ReferenceWidgetProps>()
const chips = ref<EntityReferenceData[]>([]);
const fieldUtil = new ReferenceFieldWidgetUtil(props.presenter, props.fieldMetaData, props.defaultEntityType);
const fieldId = fieldUtil.fieldId();
const isLoading = ref(false)

async function fetchChips() {
  isLoading.value = true;
  let referencedEntityType = fieldUtil.referencedEntityType.value;
  let contextInfo: FieldActionContextInfo = fieldUtil.presenter.createSearchContextInfo(fieldUtil.fieldId());
  chips.value = await fieldUtil.searchFor({
    query: "",
    ownerEntityType: fieldUtil.presenter.entityType,
    contextInfo,
    referencedEntityType,
    fieldId: fieldUtil.fieldId(),
    extraCriteria: fieldUtil.presenter.fetchCriteriaFromFilterField(referencedEntityType, fieldId)
  }) ?? [];
  isLoading.value = false;
}

function toggleChip(chip: EntityReferenceData) {
  let valueToSelect = null;
  if (ObjectChecker.areNotEqual(fieldUtil.fetchFieldValue(), chip))
    valueToSelect = chip;
  fieldUtil.onSelect(valueToSelect)
}

function isChipSelected(chip: EntityReferenceData) {
  return ObjectChecker.areEqual(chip, fieldUtil.fetchFieldValue());
}

function chipLabel(chip: EntityReferenceData): string {
  return Translator.translateRef(chip);
}

onBeforeMount(async () => await fetchChips())
</script>

<template>
  <WidgetContainer
    :field="field" :entityType="presenter.entityType" :isSpanned="isSpanned"
    :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)" :isInsideComposite="isInsideComposite"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts">
    <template #icon>
      <NamaIcon :block="field" :presenter="presenter" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget>
      <div v-if="isLoading" class="tw-flex tw-items-center tw-gap-2 tw-py-2 tw-px-2">
        <q-spinner size="16px" color="primary"/>
        <span class="tw-text-xs tw-text-black/50 dark:tw-text-white/50" v-text="Translator.translateId('loading')"/>
      </div>
      <div v-else-if="chips.length"
           class="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-md tw-p-1.5"
           :class="{'tw-border tw-border-black/10 dark:tw-border-white/20': !(field as any)?.hideBorder}">
        <div v-for="chip in chips"
             :key="chip.id"
             @click="toggleChip(chip)"
             class="tw-relative tw-grow tw-overflow-hidden tw-rounded-md tw-select-none
                    tw-bg-gray-600/40 hover:tw-bg-gray-600/50
                    tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-1 tw-shadow-md tw-cursor-pointer">
          <span
            :class="{'!tw-opacity-100': isChipSelected(chip)}"
            class="tw-absolute tw-inset-0 tw-opacity-[0.15] dark:tw-opacity-[0.08] -tw-z-0 tw-w-full tw-h-full tw-bg-primary-light dark:tw-bg-primary-dark tw-transition-opacity"/>
          <span
            v-text="chipLabel(chip)"
            class="tw-text-white dark:tw-text-white/90 tw-text-[12px] tw-z-[1]"/>
        </div>
      </div>
      <div v-else class="tw-py-2 tw-px-2">
        <span class="tw-text-xs tw-text-black/50 dark:tw-text-white/50" v-text="Translator.translateId('noResults')"/>
      </div>
    </template>
  </WidgetContainer>
</template>
